import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import useApiClient from "../../lib/useApiClient";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import { SectionCardHeader } from "../../components/ChartCard";
import {
  STATS_SQL, DOC_LIST_SQL,
  buildDocDetailSql,
  mapStatsRows, mapDocListRows, mapDocDetailRows,
} from "./sql";
import type { DocListRow, DocDetailRow, StatsRow, StatsPeriod } from "./sql";
import { runAnalysis } from "./analysis";
import type { AnalysisResult, Finding } from "./analysis";

function stripMarks(html: string): string {
  return html.replace(/<mark[^>]*>([\s\S]*?)<\/mark>/g, "$1");
}

function fmtK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtUsd(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 1000) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

function fmtUsdFull(n: number): string {
  if (!isFinite(n)) return "—";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDuration(ms: number): string {
  if (!isFinite(ms) || ms <= 0) return "—";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}m ${r}s`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

// ---- paragraph-level diff helpers ----

function wordSet(text: string): Set<string> {
  return new Set(text.toLowerCase().split(/\W+/).filter((w) => w.length > 3));
}

function jaccardSim(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1;
  let inter = 0;
  for (const w of a) if (b.has(w)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 1 : inter / union;
}

const BLOCK_TAGS = new Set(["H1","H2","H3","H4","H5","H6","P","LI","TD","TH","BLOCKQUOTE","DT","DD"]);

function extractTextBlocks(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const results: string[] = [];
  function walk(el: Element) {
    if (BLOCK_TAGS.has(el.tagName)) {
      const t = el.textContent?.trim() ?? "";
      if (t.length >= 8) results.push(t.toLowerCase());
      return;
    }
    for (const c of el.children) walk(c);
  }
  for (const c of doc.body.children) walk(c);
  return results;
}

function applyDiff(docHtml: string, embHtml: string): string {
  const docBlocks = extractTextBlocks(stripMarks(docHtml));
  const docSets = docBlocks.map(wordSet);

  const embDoc = new DOMParser().parseFromString(embHtml, "text/html");

  function markDiff(el: Element) {
    if (BLOCK_TAGS.has(el.tagName)) {
      const t = (el.textContent?.trim() ?? "").toLowerCase();
      if (t.length >= 8) {
        const ws = wordSet(t);
        const best = docSets.length === 0 ? 1 : Math.max(0, ...docSets.map((ds) => jaccardSim(ws, ds)));
        if (best < 0.35) {
          const s = (el as HTMLElement).style;
          s.background = "rgba(239,68,68,0.1)";
          s.borderLeft = "3px solid rgba(239,68,68,0.5)";
          s.paddingLeft = "8px";
          s.marginLeft = "-11px";
          s.borderRadius = "2px";
        }
      }
    } else {
      for (const c of el.children) markDiff(c);
    }
  }

  for (const c of embDoc.body.children) markDiff(c);
  return embDoc.body.innerHTML;
}

// ---- main page ----

type AnalysisState = "loading" | AnalysisResult;

export function EmbeddingComparison() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [promptDoc, setPromptDoc] = useState<DocListRow | null>(null);
  const [search, setSearch] = useState("");
  const [analysisMap, setAnalysisMap] = useState<Map<string, AnalysisState>>(new Map());
  const [analysisModal, setAnalysisModal] = useState<{ doc: DocListRow; result: AnalysisResult } | null>(null);

  const apiClient = useApiClient();

  const stats   = useAdminQuery(STATS_SQL);
  const docList = useAdminQuery(DOC_LIST_SQL);

  const docDetailSql = useMemo(() => buildDocDetailSql(selectedId), [selectedId]);
  const docDetail    = useAdminQuery(docDetailSql);

  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>("all");

  const statsRows = useMemo(() => {
    if (!stats.data) return [];
    return mapStatsRows(stats.data.columns, stats.data.rows as unknown[][]);
  }, [stats.data]);

  const statsMap = useMemo(
    () => Object.fromEntries(statsRows.map((r: StatsRow) => [r.period, r])) as Partial<Record<StatsPeriod, StatsRow>>,
    [statsRows],
  );

  const docListRows = useMemo<DocListRow[]>(() => {
    if (!docList.data) return [];
    return mapDocListRows(docList.data.columns, docList.data.rows as unknown[][]);
  }, [docList.data]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return docListRows;
    return docListRows.filter((r: DocListRow) => r.name.toLowerCase().includes(q));
  }, [docListRows, search]);

  const docDetailRow = useMemo<DocDetailRow | null>(() => {
    if (!docDetail.data) return null;
    const rows = mapDocDetailRows(docDetail.data.columns, docDetail.data.rows as unknown[][]);
    return rows[0] ?? null;
  }, [docDetail.data]);

  const selectedDoc = useMemo(
    () => docListRows.find((r: DocListRow) => r.external_id === selectedId) ?? null,
    [docListRows, selectedId],
  );

  async function analyseDoc(doc: DocListRow) {
    setAnalysisMap((prev: Map<string, AnalysisState>) => new Map(prev).set(doc.external_id, "loading"));
    try {
      const sql = buildDocDetailSql(doc.external_id);
      const { data, error } = await apiClient.POST("/admin/query", {
        body: { sql, parameters: [] },
      });
      if (error || !data) throw new Error("Query failed");
      const rows = mapDocDetailRows(data.columns as string[], data.rows as unknown[][]);
      const detail = rows[0];
      if (detail?.embeddings_content) {
        const result = runAnalysis(detail.doc_content, detail.embeddings_content);
        setAnalysisMap((prev: Map<string, AnalysisState>) => new Map(prev).set(doc.external_id, result));
      } else {
        setAnalysisMap((prev: Map<string, AnalysisState>) => { const m = new Map(prev); m.delete(doc.external_id); return m; });
      }
    } catch {
      setAnalysisMap((prev: Map<string, AnalysisState>) => { const m = new Map(prev); m.delete(doc.external_id); return m; });
    }
  }

  const isLoading = stats.isLoading || docList.isLoading;

  if (selectedId) {
    return (
      <CompareView
        doc={selectedDoc}
        htmlContent={docDetailRow?.doc_content ?? null}
        embeddingsContent={docDetailRow?.embeddings_content ?? null}
        templateContent={docDetailRow?.template_content ?? null}
        templateName={docDetailRow?.template_name ?? null}
        isLoading={docDetail.isLoading}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <>
      <Topbar crumbs={["Dashboards", "Embedding Comparison"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Operations · public.document + public.embedding_chunk</div>
            <h1>Document Embedding Comparison</h1>
            <p className="lede">
              Side-by-side view of the AI-generated document output vs the plain-text content
              stored in embedding chunks for RAG retrieval. Select a document to compare.
            </p>
          </div>
        </div>

        {isLoading && docListRows.length === 0 ? (
          <LoadingPanel />
        ) : (
          <>
            <CostComparisonCard statsMap={statsMap} period={statsPeriod} onPeriodChange={setStatsPeriod} />

            <div className="card" style={{ marginTop: 16 }}>
              <SectionCardHeader
                title="Documents"
                subtitle={`${filteredRows.length} of ${docListRows.length} shown. Click a row to open the full-page comparison.`}
                headerExtra={
                  <input
                    type="search"
                    placeholder="Filter by name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      fontSize: 12,
                      padding: "4px 8px",
                      border: "1px solid var(--border-default)",
                      borderRadius: 4,
                      background: "var(--bg-1)",
                      color: "var(--fg-1)",
                      width: 200,
                    }}
                  />
                }
              />
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Generated by</th>
                        <th>Status</th>
                        <th>Model</th>
                        <th>Created</th>
                        <th className="num">Gen cost</th>
                        <th className="num">Content</th>
                        <th className="num">Chunks</th>
                        <th className="num">Embedding</th>
                        <th className="num">Unfilled Δ</th>
                        <th>Analysis</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r) => (
                        <tr
                          key={r.external_id}
                          onClick={() => setSelectedId(r.external_id)}
                          style={{ cursor: "pointer" }}
                        >
                          <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {r.name}
                          </td>
                          <td style={{ fontSize: 12, color: "var(--fg-2)" }}>
                            {r.user_name || r.user_email || "—"}
                          </td>
                          <td><StatusBadge status={r.status} /></td>
                          <td style={{ color: "var(--fg-3)", fontSize: 11 }}>{r.model}</td>
                          <td style={{ color: "var(--fg-3)" }}>{fmtDate(r.created_date_utc)}</td>
                          <td className="num mono">
                            {r.doc_cost_usd > 0
                              ? <span style={{ color: "var(--fg-1)" }}>{fmtUsd(r.doc_cost_usd)}</span>
                              : <span style={{ color: "var(--fg-3)" }}>—</span>}
                          </td>
                          <td className="num mono">{fmtK(r.content_chars)} ch</td>
                          <td className="num">
                            {r.chunk_count > 0 ? (
                              <span style={{ color: "var(--color-jade, #1E6D5B)", fontWeight: 600 }}>
                                {r.chunk_count}
                              </span>
                            ) : (
                              <span style={{ color: "var(--fg-3)" }}>—</span>
                            )}
                          </td>
                          <td className="num mono">
                            {r.embedding_chars > 0
                              ? `${fmtK(r.embedding_chars)} ch`
                              : <span style={{ color: "var(--fg-3)" }}>—</span>}
                          </td>
                          <td className="num">
                            <UnfilledDiff row={r} />
                          </td>
                          <td>
                            <AnalysisBadge
                              state={analysisMap.get(r.external_id)}
                              onAnalyse={(e: ReactMouseEvent<HTMLButtonElement>) => { e.stopPropagation(); analyseDoc(r); }}
                              onOpen={(e: ReactMouseEvent<HTMLButtonElement>, result: AnalysisResult) => { e.stopPropagation(); setAnalysisModal({ doc: r, result }); }}
                            />
                          </td>
                          <td>
                            {r.prompt && (
                              <button
                                type="button"
                                onClick={(e: ReactMouseEvent<HTMLButtonElement>) => { e.stopPropagation(); setPromptDoc(r); }}
                                style={{
                                  fontSize: 11,
                                  padding: "2px 7px",
                                  border: "1px solid var(--border-default)",
                                  borderRadius: 3,
                                  background: "var(--bg-1)",
                                  color: "var(--fg-2)",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Prompt
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {promptDoc && (
        <PromptModal doc={promptDoc} onClose={() => setPromptDoc(null)} />
      )}
      {analysisModal && (
        <AnalysisModal
          doc={analysisModal.doc}
          result={analysisModal.result}
          onClose={() => setAnalysisModal(null)}
        />
      )}
    </>
  );
}

const PERIOD_LABELS: Record<StatsPeriod, string> = {
  all:      "All time",
  last_30d: "Last 30 days",
  last_7d:  "Last 7 days",
};

function CostComparisonCard({
  statsMap,
  period,
  onPeriodChange,
}: {
  statsMap: Partial<Record<StatsPeriod, StatsRow>>;
  period: StatsPeriod;
  onPeriodChange: (p: StatsPeriod) => void;
}) {
  const statsRow = statsMap[period] ?? null;
  if (!statsRow) return null;
  const docAvg = statsRow.doc_job_count > 0 ? statsRow.doc_total_cost / statsRow.doc_job_count : 0;
  const embAvg = statsRow.emb_job_count > 0 ? statsRow.emb_total_cost / statsRow.emb_job_count : 0;
  const savingsPct = docAvg > 0 ? Math.round((1 - embAvg / docAvg) * 100) : 0;
  const savingsPerJob = docAvg - embAvg;

  const panelStyle: CSSProperties = { padding: "20px 24px" };
  const labelStyle: CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--fg-3)", marginBottom: 12 };
  const codeStyle: CSSProperties = { marginLeft: 8, fontWeight: 400, fontFamily: "var(--font-mono)", fontSize: 10 };
  const bigStyle: CSSProperties = { fontSize: 30, fontWeight: 700, color: "var(--fg-1)", marginBottom: 2 };
  const subStyle: CSSProperties = { fontSize: 12, color: "var(--fg-3)", marginBottom: 16 };
  const workingStyle: CSSProperties = { fontSize: 12, color: "var(--fg-2)", background: "var(--bg-2)", borderRadius: 4, padding: "8px 12px", fontFamily: "var(--font-mono)" };
  const metaStyle: CSSProperties = { marginTop: 10, fontSize: 12, color: "var(--fg-3)" };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <SectionCardHeader
        title="Cost Comparison"
        subtitle="Full document generation vs embedding generation — from audit.llm_usage"
        headerExtra={
          <div style={{ display: "flex", gap: 4 }}>
            {(Object.keys(PERIOD_LABELS) as StatsPeriod[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => onPeriodChange(key)}
                style={{
                  fontSize: 11, padding: "3px 10px",
                  border: "1px solid var(--border-default)", borderRadius: 3, cursor: "pointer",
                  background: period === key ? "var(--bg-3, #e8e8e8)" : "var(--bg-1)",
                  color: period === key ? "var(--fg-1)" : "var(--fg-3)",
                  fontWeight: period === key ? 600 : 400,
                }}
              >
                {PERIOD_LABELS[key]}
              </button>
            ))}
          </div>
        }
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 1fr" }}>
        <div style={panelStyle}>
          <div style={labelStyle}>
            Full Document Generation<span style={codeStyle}>document_generation</span>
          </div>
          <div style={bigStyle}>{fmtUsd(docAvg)}</div>
          <div style={subStyle}>per job average</div>
          <div style={workingStyle}>
            {fmtUsdFull(statsRow.doc_total_cost)} ÷ {statsRow.doc_job_count.toLocaleString()} jobs = {fmtUsd(docAvg)}
          </div>
          <div style={metaStyle}>
            {fmtK(statsRow.doc_job_count)} jobs · {fmtK(Math.round(statsRow.doc_avg_tokens))} tokens avg · {fmtDuration(statsRow.doc_avg_duration_ms)}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 12px", borderLeft: "1px solid var(--border-default)", borderRight: "1px solid var(--border-default)", textAlign: "center" }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--color-jade, #1E6D5B)" }}>
            {savingsPct > 0 ? `${savingsPct}%` : "—"}
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>cheaper</div>
          {savingsPerJob > 0 && (
            <div style={{ fontSize: 12, color: "var(--fg-2)", marginTop: 10, lineHeight: 1.5 }}>
              saves {fmtUsd(savingsPerJob)}<br />per job
            </div>
          )}
        </div>

        <div style={panelStyle}>
          <div style={labelStyle}>
            Embedding Generation<span style={codeStyle}>document_generation_embeddings</span>
          </div>
          <div style={bigStyle}>{fmtUsd(embAvg)}</div>
          <div style={subStyle}>per job average</div>
          <div style={workingStyle}>
            {fmtUsdFull(statsRow.emb_total_cost)} ÷ {statsRow.emb_job_count.toLocaleString()} jobs = {fmtUsd(embAvg)}
          </div>
          <div style={metaStyle}>
            {fmtK(statsRow.emb_job_count)} jobs · {fmtK(Math.round(statsRow.emb_avg_tokens))} tokens avg · {fmtDuration(statsRow.emb_avg_duration_ms)}
          </div>
        </div>
      </div>
    </div>
  );
}

function PromptModal({ doc, onClose }: { doc: DocListRow; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e: ReactMouseEvent<HTMLDivElement>) => e.stopPropagation()}
        style={{
          background: "var(--bg-1)",
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          width: "min(680px, 90vw)",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "14px 18px",
          borderBottom: "1px solid var(--border-default)",
          flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {doc.name}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>Initial prompt</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--fg-3)", fontSize: 16, padding: "2px 4px", flexShrink: 0,
            }}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "18px" }}>
          <pre style={{
            margin: 0, fontSize: 13, lineHeight: 1.65,
            whiteSpace: "pre-wrap", wordBreak: "break-word",
            color: "var(--fg-1)", fontFamily: "var(--font-mono, monospace)",
          }}>
            {doc.prompt}
          </pre>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_LABELS: Record<Finding["category"], string> = {
  instruction_leak: "Instruction Leakage",
  placeholder:      "Unfilled Placeholders",
  numeric:          "Data Field Differences",
  structure:        "Section Structure",
};

const SEVERITY_COLOR: Record<Finding["severity"], string> = {
  high:   "var(--color-coral-hover, #DC6A4C)",
  medium: "#D97706",
  low:    "var(--fg-3)",
};

function worstSeverity(findings: Finding[]): Finding["severity"] | null {
  if (findings.some((f) => f.severity === "high")) return "high";
  if (findings.some((f) => f.severity === "medium")) return "medium";
  if (findings.length > 0) return "low";
  return null;
}

const PILL_STYLE: CSSProperties = {
  fontSize: 11,
  padding: "2px 7px",
  border: "1px solid var(--border-default)",
  borderRadius: 3,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

function AnalysisBadge({
  state,
  onAnalyse,
  onOpen,
}: {
  state: AnalysisState | undefined;
  onAnalyse: (e: ReactMouseEvent<HTMLButtonElement>) => void;
  onOpen: (e: ReactMouseEvent<HTMLButtonElement>, result: AnalysisResult) => void;
}) {
  if (!state) {
    return (
      <button type="button" onClick={onAnalyse} style={{ ...PILL_STYLE, background: "var(--bg-1)", color: "var(--fg-2)" }}>
        Analyse
      </button>
    );
  }
  if (state === "loading") {
    return <span style={{ fontSize: 11, color: "var(--fg-3)" }}>Analysing...</span>;
  }
  const worst = worstSeverity(state.findings);
  if (!worst) {
    return (
      <button type="button" onClick={(e) => onOpen(e, state)} style={{ ...PILL_STYLE, background: "rgba(16,185,129,0.1)", color: "var(--color-jade, #1E6D5B)", border: "1px solid rgba(16,185,129,0.3)", fontWeight: 600 }}>
        Clean
      </button>
    );
  }
  const color = SEVERITY_COLOR[worst];
  const n = state.findings.length;
  return (
    <button type="button" onClick={(e) => onOpen(e, state)} style={{ ...PILL_STYLE, background: `rgba(220,106,76,0.08)`, color, border: `1px solid ${color}`, fontWeight: 600 }}>
      {n} {n === 1 ? "issue" : "issues"}
    </button>
  );
}

function AnalysisModal({
  doc,
  result,
  onClose,
}: {
  doc: DocListRow;
  result: AnalysisResult;
  onClose: () => void;
}) {
  const grouped = Object.fromEntries(
    (Object.keys(CATEGORY_LABELS) as Finding["category"][]).map((cat) => [
      cat,
      result.findings.filter((f) => f.category === cat),
    ])
  ) as Record<Finding["category"], Finding[]>;

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
    >
      <div
        onClick={(e: ReactMouseEvent<HTMLDivElement>) => e.stopPropagation()}
        style={{ background: "var(--bg-1)", border: "1px solid var(--border-default)", borderRadius: 8, width: "min(720px, 92vw)", maxHeight: "78vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: "1px solid var(--border-default)", flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--fg-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {doc.name}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>
              Document quality analysis
              {result.findings.length === 0
                ? " — no issues found"
                : ` — ${result.findings.length} ${result.findings.length === 1 ? "issue" : "issues"} found`}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--fg-3)", fontSize: 16, padding: "2px 4px" }}>
            <i className="ti ti-x" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "18px" }}>
          {(Object.keys(CATEGORY_LABELS) as Finding["category"][]).map((cat) => (
            <div key={cat} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--fg-3)", marginBottom: 8 }}>
                {CATEGORY_LABELS[cat]}
              </div>
              {grouped[cat].length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--fg-3)", fontStyle: "italic", paddingLeft: 2 }}>
                  No significant differences found
                </div>
              ) : (
                grouped[cat].map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid var(--border-default)" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: SEVERITY_COLOR[f.severity], flexShrink: 0, marginTop: 4 }} />
                    <div>
                      <div style={{ fontSize: 13, color: "var(--fg-1)", fontWeight: 500 }}>{f.label}</div>
                      {f.detail && (
                        <div style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 2, fontFamily: "var(--font-mono, monospace)" }}>{f.detail}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 18px", borderTop: "1px solid var(--border-default)", fontSize: 11, color: "var(--fg-3)", display: "flex", gap: 16, flexShrink: 0 }}>
          <span>Doc output: {result.docWordCount.toLocaleString()} words</span>
          <span>Embedding: {result.embWordCount.toLocaleString()} words</span>
          {result.docWordCount > 0 && (
            <span>Ratio: {Math.round(result.embWordCount / result.docWordCount * 100)}%</span>
          )}
        </div>
      </div>
    </div>
  );
}

function UnfilledDiff({ row }: { row: DocListRow }) {
  const tbcDiff = row.emb_tbc - row.doc_tbc;
  const reqDiff = row.emb_req - row.doc_req;
  if (tbcDiff === 0 && reqDiff === 0) {
    return <span style={{ color: "var(--fg-3)" }}>—</span>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
      {(row.emb_tbc > 0 || row.doc_tbc > 0) && (
        <span style={{
          fontSize: 11,
          color: tbcDiff > 0 ? "var(--color-coral-hover, #DC6A4C)" : tbcDiff < 0 ? "var(--fg-3)" : "var(--fg-2)",
          fontWeight: tbcDiff !== 0 ? 600 : 400,
        }}>
          TBC {tbcDiff > 0 ? "+" : ""}{tbcDiff} ({row.emb_tbc}/{row.doc_tbc})
        </span>
      )}
      {(row.emb_req > 0 || row.doc_req > 0) && (
        <span style={{
          fontSize: 11,
          color: reqDiff > 0 ? "var(--color-coral-hover, #DC6A4C)" : reqDiff < 0 ? "var(--fg-3)" : "var(--fg-2)",
          fontWeight: reqDiff !== 0 ? 600 : 400,
        }}>
          REQ {reqDiff > 0 ? "+" : ""}{reqDiff} ({row.emb_req}/{row.doc_req})
        </span>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "completed" ? "var(--color-jade, #1E6D5B)" :
    status === "failed"    ? "var(--color-coral-hover, #DC6A4C)" :
    "var(--fg-3)";
  return (
    <span style={{ fontSize: 11, color, fontWeight: 500, textTransform: "capitalize" }}>
      {status}
    </span>
  );
}

function CompareView({
  doc, htmlContent, embeddingsContent, templateContent, templateName, isLoading, onBack,
}: {
  doc: DocListRow | null;
  htmlContent: string | null;
  embeddingsContent: string | null;
  templateContent: string | null;
  templateName: string | null;
  isLoading: boolean;
  onBack: () => void;
}) {
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [syncScroll, setSyncScroll] = useState(true);
  const [widths, setWidths] = useState([33.33, 33.33, 33.34]);
  const [dragging, setDragging] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const leftRef   = useRef<HTMLDivElement>(null); // template
  const middleRef = useRef<HTMLDivElement>(null); // document v1
  const rightRef  = useRef<HTMLDivElement>(null); // embeddings
  const syncing   = useRef(false);

  useEffect(() => {
    if (!syncScroll) return;
    const panes = [leftRef.current, middleRef.current, rightRef.current].filter(Boolean) as HTMLDivElement[];
    if (panes.length < 2) return;

    const handlers = panes.map((source, i) => {
      const handler = () => {
        if (syncing.current) return;
        syncing.current = true;
        const pct = source.scrollTop / Math.max(1, source.scrollHeight - source.clientHeight);
        panes.forEach((target, j) => {
          if (j !== i) target.scrollTop = pct * (target.scrollHeight - target.clientHeight);
        });
        syncing.current = false;
      };
      source.addEventListener("scroll", handler);
      return { source, handler };
    });

    return () => handlers.forEach(({ source, handler }) => source.removeEventListener("scroll", handler));
  }, [syncScroll]);

  const diffedEmbeddings = useMemo(() => {
    if (!htmlContent || !embeddingsContent) return embeddingsContent;
    return applyDiff(htmlContent, embeddingsContent);
  }, [htmlContent, embeddingsContent]);

  function startResize(handleIndex: number, e: ReactMouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidths = [...widths];
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 1;
    setDragging(handleIndex);

    const onMove = (me: MouseEvent) => {
      const dPct = (me.clientX - startX) / containerWidth * 100;
      const total = startWidths[handleIndex]! + startWidths[handleIndex + 1]!;
      const MIN = 10;
      const a = Math.min(total - MIN, Math.max(MIN, startWidths[handleIndex]! + dPct));
      const next = [...startWidths];
      next[handleIndex] = a;
      next[handleIndex + 1] = total - a;
      setWidths(next);
    };

    const onUp = () => {
      setDragging(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 20px",
        borderBottom: "1px solid var(--border-default)",
        background: "var(--bg-2)",
        flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            background: "var(--bg-1)",
            color: "var(--fg-2)",
            fontSize: 12,
            padding: "4px 10px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <i className="ti ti-arrow-left" style={{ fontSize: 13 }} />
          Back to list
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {doc?.name ?? "Document"}
          </div>
          {doc && (
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
              {doc.user_name || doc.user_email || "Unknown user"}
              {" · "}{doc.model} · {fmtK(doc.content_chars)} chars output
              {doc.chunk_count > 0
                ? ` · ${doc.chunk_count} chunks · ${fmtK(doc.embedding_chars)} chars embedded`
                : " · no embeddings"}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setSyncScroll(!syncScroll)}
            style={{
              fontSize: 11,
              color: syncScroll ? "var(--fg-1)" : "var(--fg-3)",
              background: syncScroll ? "var(--bg-3, #e8e8e8)" : "var(--bg-1)",
              border: "1px solid var(--border-default)",
              borderRadius: 3,
              padding: "3px 8px",
              cursor: "pointer",
            }}
          >
            <i className="ti ti-lock" style={{ fontSize: 11, marginRight: 4 }} />
            {syncScroll ? "Scroll synced" : "Scroll free"}
          </button>
          <button
            type="button"
            onClick={() => setShowRawHtml(!showRawHtml)}
            style={{
              fontSize: 11,
              color: "var(--fg-2)",
              background: "var(--bg-1)",
              border: "1px solid var(--border-default)",
              borderRadius: 3,
              padding: "3px 8px",
              cursor: "pointer",
            }}
          >
            {showRawHtml ? "Show rendered" : "Show raw HTML"}
          </button>
          <span style={{ fontSize: 11, color: "var(--fg-3)", marginLeft: 4, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{
              display: "inline-block", width: 10, height: 10,
              background: "rgba(239,68,68,0.3)", border: "1px solid rgba(239,68,68,0.5)",
              borderRadius: 2,
            }} />
            differs from doc output
          </span>
        </div>
      </div>

      {/* Three panes */}
      <div ref={containerRef} style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Pane 1: Template */}
        <div style={{
          width: `${widths[0]}%`,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <PaneLabel>{templateName ?? "Template"}</PaneLabel>
          <div ref={leftRef} style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
            {isLoading ? (
              <LoadingState />
            ) : templateContent === null ? (
              <EmptyState>No template linked to this document.</EmptyState>
            ) : (
              // Internal template HTML; safe to render.
              <div
                dangerouslySetInnerHTML={{ __html: templateContent }}
                style={{ maxWidth: 720, fontSize: 14, lineHeight: 1.7, color: "var(--fg-1)" }}
              />
            )}
          </div>
        </div>

        <div className={`drag-handle${dragging === 0 ? " is-dragging" : ""}`} onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => startResize(0, e)} />

        {/* Pane 2: Document output v1 */}
        <div style={{
          width: `${widths[1]}%`,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <PaneLabel>Document Output (v1)</PaneLabel>
          <div ref={middleRef} style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
            {isLoading ? (
              <LoadingState />
            ) : htmlContent === null ? (
              <EmptyState>No content.</EmptyState>
            ) : showRawHtml ? (
              <pre style={{
                margin: 0, fontSize: 12, lineHeight: 1.6,
                whiteSpace: "pre-wrap", wordBreak: "break-all",
                color: "var(--fg-2)", fontFamily: "var(--font-mono)",
              }}>
                {htmlContent}
              </pre>
            ) : (
              // Internal AI-generated HTML; marks stripped, safe to render.
              <div
                dangerouslySetInnerHTML={{ __html: stripMarks(htmlContent) }}
                style={{ maxWidth: 720, fontSize: 14, lineHeight: 1.7, color: "var(--fg-1)" }}
              />
            )}
          </div>
        </div>

        <div className={`drag-handle${dragging === 1 ? " is-dragging" : ""}`} onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => startResize(1, e)} />

        {/* Pane 3: Embeddings content with diff highlights */}
        <div style={{ width: `${widths[2]}%`, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <PaneLabel>Embeddings Content</PaneLabel>
          <div ref={rightRef} style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
            {isLoading ? (
              <LoadingState />
            ) : diffedEmbeddings === null ? (
              <EmptyState>No embeddings content for this document.</EmptyState>
            ) : (
              // Internal AI-generated HTML with diff highlights applied; safe to render.
              <div
                dangerouslySetInnerHTML={{ __html: diffedEmbeddings }}
                style={{ maxWidth: 720, fontSize: 14, lineHeight: 1.7, color: "var(--fg-1)" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PaneLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: "8px 16px",
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "var(--fg-3)",
      borderBottom: "1px solid var(--border-default)",
      background: "var(--bg-2)",
      flexShrink: 0,
    }}>
      {children}
    </div>
  );
}

function LoadingState() {
  return <span style={{ fontSize: 13, color: "var(--fg-3)" }}>Loading...</span>;
}

function EmptyState({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 13, color: "var(--fg-3)" }}>{children}</span>;
}
